import { createContext } from "react";

/** Logical theme. Maps to a design-system `data-theme` scope via `dsTheme()`. */
export type Theme = "light" | "dark";

export const THEME_STORAGE_KEY = "fastn-theme";

/**
 * Design-system theme scope for a logical theme.
 *
 * The product runs on `console-dark` (canvas var(--fs-bg-canvas), denser surfaces), not the
 * DS `dark` register, which is the pure-black marketing canvas. Keeping the
 * logical name "dark" means every `theme === "dark"` consumer (Monaco, Mermaid,
 * XYFlow) keeps working while the rendered scope is the correct product one.
 */
export function dsTheme(theme: Theme): "light" | "console-dark" {
  return theme === "dark" ? "console-dark" : "light";
}

/** Applies the theme to <html> and persists it. */
export function applyTheme(theme: Theme): void {
  document.documentElement.setAttribute("data-theme", dsTheme(theme));
  document.documentElement.style.colorScheme = theme;
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // Storage can be unavailable (private mode, embedded contexts). The theme
    // still applies for this session.
  }
}

/** Stored preference, falling back to the OS setting, then dark. */
export function readStoredTheme(): Theme {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === "light" || stored === "dark") return stored;
  } catch {
    // ignore
  }
  return window.matchMedia?.("(prefers-color-scheme: light)").matches ? "light" : "dark";
}

export interface ThemeContextValue {
  theme: Theme;
  toggle: () => void;
}

export const ThemeContext = createContext<ThemeContextValue>({
  theme: "dark",
  toggle: () => {},
});
